// (c) chaehongee. Confidential and proprietary.

import type { RefObject } from 'react'
import type { ItemsAPI, ImageSize } from './types'
import { useItemTransforms } from './useItemTransforms'

interface UseCanvasItemsOptions {
    containerRef: RefObject<HTMLDivElement | null>
    clientToWorld: (clientX: number, clientY: number) => { x: number; y: number } | null
    imageRectInContainer: { x: number; y: number; w: number; h: number } | null
    multiSelectionNodes: Array<{ id: string; src: string; mediaType?: string }>
    imageSizesBySrc: Record<string, ImageSize>
    disableAutoRowReflow?: boolean
    suppressInitialPlacement?: boolean
    onImageRectChange?: (id: string, rect: { x: number; y: number; w: number; h: number }) => void
    sessionId?: string | null
    dispatch?: (action: any) => void
    onImageResizeCommit?: (info: { id: string; rect: { x: number; y: number; w: number; h: number }; src?: string }) => void
    selectedNodeIds?: string[]
    toolMode?: 'pointer' | 'grab' | 'select'
}

/**
 * Canvas Items Feature
 * 
 * Manages canvas items positioning and transforms (move/resize).
 * This is the main API for the items feature.
 * 
 * Note: Image size tracking is handled separately via useImageSizes
 * to avoid circular dependencies with other features.
 */
export function useCanvasItems(options: UseCanvasItemsOptions): Omit<ItemsAPI, 'imageSizesBySrc' | 'handleImageLoad'> {
    const {
        containerRef,
        clientToWorld,
        imageRectInContainer,
        multiSelectionNodes,
        imageSizesBySrc,
        disableAutoRowReflow,
        suppressInitialPlacement,
        onImageRectChange,
        sessionId,
        dispatch,
        onImageResizeCommit,
        selectedNodeIds,
        toolMode,
    } = options

    // Item transforms (positioning, resize, move)
    return useItemTransforms({
        containerRef,
        clientToWorld,
        imageRectInContainer,
        imageSizesBySrc,
        multiSelectionNodes,
        disableAutoRowReflow,
        suppressInitialPlacement,
        onImageRectChange,
        sessionId,
        dispatch,
        onImageResizeCommit,
        selectedNodeIds,
        toolMode,
    })
}

